import CommandInterceptor from "diagram-js/lib/command/CommandInterceptor";
import EventBus from "diagram-js/lib/core/EventBus";
import Modeling from "diagram-js/lib/features/modeling/Modeling";
import { Shape } from "diagram-js/lib/model"; // Import the Shape type
import { FrendsElementStore } from "processEditor/model/FrendsElementStore";

interface ShapeCreateContext {
    shape: Shape;
}

class ShapeCreateNameUpdateInterceptor extends CommandInterceptor {
    private readonly skippedElements = ["bpmn:TextAnnotation"];
    constructor(eventBus: EventBus, modeling: Modeling, frendsStore: FrendsElementStore) {
        super(eventBus);

        this.postExecuted(["shape.create"], ({ context }: { context: ShapeCreateContext }): void => {
            const { shape } = context;
            if (this.skippedElements.includes(shape.type)) {
                return;
            }

            let currentName = shape.businessObject.name;

            if (shape.type === "bpmn:Group" && !currentName?.trim().length) {
                // Groups must initially have non-blank label
                // https://github.com/bpmn-io/bpmn-js/issues/2231
                // Can be removed once we are on bpmn 18.0.0
                currentName = "Group";
            }

            // This is not a copied element and name is not set
            // We have a small issue that when you create a element and change the name from the parameter editor
            // there is a timing issue when you de-select the shape it might update the name back to empty.
            // This workaround sets the name to empty string which seems to be enough for bpmn-js to not update the value after de-selecting a newly created shape
            if (currentName === null) {
                modeling.updateLabel(shape, "");

                return;
            }

            // Because we don't allow duplicate names we can now use this to increment the name with a (n+1)
            const currentElements = frendsStore.getElements();

            let nameCounter = 0;
            let hasDuplicate = false;
            for (const key in currentElements) {
                const element = currentElements[key];
                if (element.name == null) {
                    continue;
                }
                const regex = new RegExp(`^${currentName}( \\d+)?$`); // Matches "Foo" or "Foo 1", "Foo 2", etc.

                if (regex.test(element.name)) {
                    hasDuplicate = true;  // Mark that a duplicate was found

                    // Extract the numerical suffix if it exists
                    const nameSuffix = element.name.slice(currentName.length).trim();

                    // If the suffix is a number, compare and track the highest number
                    const suffixNumber = parseInt(nameSuffix);
                    if (!isNaN(suffixNumber)) {
                        nameCounter = Math.max(nameCounter, suffixNumber);
                    }
                }
            }

            // If there were duplicates, increment the name.
            const newName = hasDuplicate ? `${currentName} ${nameCounter + 1}` : currentName;
            modeling.updateLabel(shape, newName);
        });
    }
}

ShapeCreateNameUpdateInterceptor.$inject = ["eventBus", "modeling", "frendsStore"];

export default {
    __init__: ["myCommandInterceptor"],
    myCommandInterceptor: ["type", ShapeCreateNameUpdateInterceptor]
};
